import React, { useEffect, useState, useCallback, useRef } from "react";
import API from "../../../services/api";

const CONTACT_TYPES = [
  "Worker",
  "Leader",
  "Booth Member",
  "Booth President",
  "Volunteer",
  "Member",
  "Vice President",
  "Secretary",
  "Treasurer",
  "President",
];

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [villList, setVillList] = useState([]);
  const [wingList, setWingList] = useState([]);
  const [casteList, setCasteList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterVill, setFilterVill] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Village autocomplete
  const [villSearch, setVillSearch] = useState("");
  const [villDropdown, setVillDropdown] = useState([]);
  const [showVillDrop, setShowVillDrop] = useState(false);
  const [selectedVill, setSelectedVill] = useState(null);
  const villRef = useRef(null);

  // Voter search
  const [voterSearch, setVoterSearch] = useState("");
  const [voterList, setVoterList] = useState([]);
  const [voterLoading, setVoterLoading] = useState(false);
  const [showVoterDrop, setShowVoterDrop] = useState(false);
  const voterRef = useRef(null);
  const searchTimer = useRef(null);

  const emptyForm = {
    name: "",
    last_name: "",
    epic_no: "",
    mobile_no: "",
    villageid: "",
    wingid: "",
    casteid: "",
    contact_type: "Worker",
  };
  const [form, setForm] = useState(emptyForm);

  // ── Fetch Masters ─────────────────────────────────────────
  const fetchMasters = useCallback(async () => {
    try {
      const [vill, wings, castes] = await Promise.all([
        API.get("/masters/villages"),
        API.get("/masters/wings"),
        API.get("/masters/castes"),
      ]);
      setVillList(vill.data.data);
      setWingList(wings.data.data);
      setCasteList(castes.data.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  // ── Fetch Contacts ────────────────────────────────────────
  const fetchContacts = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const res = await API.get("/contacts/workers", {
          params: {
            page,
            per_page: 10,
            search: search || undefined,
            contact_type: filterType || undefined,
            villageid: filterVill || undefined,
          },
        });
        setContacts(res.data.data.contacts);
        setTotalPages(res.data.data.pagination.total_pages);
        setTotalRecords(res.data.data.pagination.total);
        setCurrentPage(page);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [search, filterType, filterVill],
  );

  useEffect(() => {
    fetchMasters();
    fetchContacts();
  }, []);

  // ── Close dropdowns on outside click ─────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (villRef.current && !villRef.current.contains(e.target))
        setShowVillDrop(false);
      if (voterRef.current && !voterRef.current.contains(e.target))
        setShowVoterDrop(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Village Autocomplete ──────────────────────────────────
  const handleVillSearch = (val) => {
    setVillSearch(val);
    setSelectedVill(null);
    setForm((prev) => ({ ...prev, villageid: "" }));
    // Reset voter search when village changes
    setVoterSearch("");
    setVoterList([]);
    setShowVoterDrop(false);

    if (val.length >= 1) {
      const filtered = villList.filter((v) =>
        v.name.toLowerCase().includes(val.toLowerCase()),
      );
      setVillDropdown(filtered.slice(0, 8));
      setShowVillDrop(true);
    } else {
      setVillDropdown([]);
      setShowVillDrop(false);
    }
  };

  const selectVillage = (village) => {
    setVillSearch(village.name);
    setSelectedVill(village);
    setForm((prev) => ({ ...prev, villageid: village.villageid }));
    setShowVillDrop(false);
    setVillDropdown([]);
    // Reset voter when village changes
    setVoterSearch("");
    setVoterList([]);
    setShowVoterDrop(false);
  };

  // ── Voter Search with debounce ────────────────────────────
  const handleVoterSearch = (val) => {
    setVoterSearch(val);
    setShowVoterDrop(true);

    if (searchTimer.current) clearTimeout(searchTimer.current);

    if (val.length >= 2) {
      setVoterLoading(true);
      searchTimer.current = setTimeout(async () => {
        try {
          const res = await API.get("/voters/", {
            params: {
              page: 1,
              per_page: 15,
              search: val,
              // filter by booth_no if village selected
              booth_no: undefined,
            },
          });
          // If village selected, filter results by booth_no matching that village
          let voters = res.data.data.voters || [];
          setVoterList(voters);
        } catch (err) {
          console.error(err);
          setVoterList([]);
        } finally {
          setVoterLoading(false);
        }
      }, 400);
    } else {
      setVoterList([]);
      setVoterLoading(false);
    }
  };

  // ── Select Voter → bind to form ───────────────────────────
  const selectVoter = (voter) => {
    setForm((prev) => ({
      ...prev,
      name: voter.firstname_en || "",
      last_name: voter.lastname_en || "",
      epic_no: voter.epic_no || "",
      mobile_no: voter.mobile || "",
    }));
    setVoterSearch(
      `${voter.firstname_en || ""} ${voter.lastname_en || ""}`.trim(),
    );
    setShowVoterDrop(false);
    setVoterList([]);
  };

  // ── Open Add ──────────────────────────────────────────────
  const openAdd = () => {
    setEditItem(null);
    setForm(emptyForm);
    setVillSearch("");
    setSelectedVill(null);
    setVoterSearch("");
    setVoterList([]);
    setShowVillDrop(false);
    setShowVoterDrop(false);
    setShowModal(true);
  };

  // ── Open Edit ─────────────────────────────────────────────
  const openEdit = (item) => {
    setEditItem(item);
    const vill = villList.find((v) => v.villageid === item.villageid);
    setVillSearch(vill ? vill.name : "");
    setSelectedVill(vill || null);
    setVoterSearch(`${item.name || ""} ${item.last_name || ""}`.trim());
    setVoterList([]);
    setShowVillDrop(false);
    setShowVoterDrop(false);
    setForm({
      name: item.name || "",
      last_name: item.last_name || "",
      epic_no: item.epic_no || "",
      mobile_no: item.mobile_no || "",
      villageid: item.villageid || "",
      wingid: item.wingid || "",
      casteid: item.casteid || "",
      contact_type: item.contact_type || "Worker",
    });
    setShowModal(true);
  };

  // ── Save ──────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name) return alert("Name is required");
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        last_name: form.last_name || null,
        epic_no: form.epic_no || null,
        mobile_no: form.mobile_no || null,
        villageid: form.villageid ? parseInt(form.villageid) : null,
        wingid: form.wingid ? parseInt(form.wingid) : null,
        casteid: form.casteid ? parseInt(form.casteid) : null,
        contact_type: form.contact_type,
      };
      if (editItem) {
        await API.put(
          `/contacts/workers/${editItem.worker_contact_id}`,
          payload,
        );
      } else {
        await API.post("/contacts/workers", payload);
      }
      setShowModal(false);
      fetchContacts(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Error saving contact");
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    setDeleting(id);
    try {
      await API.delete(`/contacts/workers/${id}`);
      fetchContacts(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Error");
    } finally {
      setDeleting(null);
    }
  };

  const contactTypeBadge = (type) => {
    const colors = {
      Worker: "bg-primary-subtle text-primary",
      Leader: "bg-warning-subtle text-warning",
      "Booth Member": "bg-info-subtle text-info",
      "Booth President": "bg-success-subtle text-success",
      Volunteer: "bg-secondary-subtle text-secondary",
      Member: "bg-info-subtle text-info",
      "Vice President": "bg-danger-subtle text-danger",
      Secretary: "bg-warning-subtle text-warning",
      Treasurer: "bg-dark-subtle text-dark",
      President: "bg-success-subtle text-success",
    };
    return colors[type] || "bg-secondary-subtle text-secondary";
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="fw-bold mb-1">Contacts Management</h4>
          <p className="text-muted small mb-0">
            Total <strong>{totalRecords}</strong> contacts
          </p>
        </div>
        <button
          className="btn text-white fw-semibold px-4"
          style={{ background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)" }}
          onClick={openAdd}
        >
          <i className="bi bi-person-plus me-2"></i>Add Contact
        </button>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm rounded-4 mb-3">
        <div className="card-body p-3">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, EPIC, mobile..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-6 col-md-3">
              <select
                className="form-select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="">All Types</option>
                {CONTACT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-2">
              <select
                className="form-select"
                value={filterVill}
                onChange={(e) => setFilterVill(e.target.value)}
              >
                <option value="">All Villages</option>
                {villList.map((v) => (
                  <option key={v.villageid} value={v.villageid}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-2">
              <button
                className="btn btn-primary w-100"
                onClick={() => fetchContacts(1)}
              >
                <i className="bi bi-search me-1"></i>Search
              </button>
            </div>
            <div className="col-6 col-md-1">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setSearch("");
                  setFilterType("");
                  setFilterVill("");
                  fetchContacts(1);
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-people fs-1 d-block mb-2"></i>
              No contacts found.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="px-4 py-3 small text-muted fw-semibold">
                      #
                    </th>
                    <th className="py-3 small text-muted fw-semibold">Name</th>
                    <th className="py-3 small text-muted fw-semibold">
                      EPIC No
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Mobile
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Contact Type
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Village
                    </th>
                    <th className="py-3 small text-muted fw-semibold text-end pe-4">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c, idx) => (
                    <tr key={c.worker_contact_id}>
                      <td className="px-4 text-muted small">
                        {(currentPage - 1) * 10 + idx + 1}
                      </td>
                      <td>
                        <div className="fw-semibold small">
                          {c.name} {c.last_name || ""}
                        </div>
                      </td>
                      <td>
                        {c.epic_no ? (
                          <span className="badge bg-primary-subtle text-primary small">
                            {c.epic_no}
                          </span>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                      <td className="small text-muted">{c.mobile_no || "—"}</td>
                      <td>
                        <span
                          className={`badge small ${contactTypeBadge(c.contact_type)}`}
                        >
                          {c.contact_type}
                        </span>
                      </td>
                      <td className="small text-muted">
                        {villList.find((v) => v.villageid === c.villageid)
                          ?.name || "—"}
                      </td>
                      <td className="text-end pe-4">
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => openEdit(c)}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(c.worker_contact_id)}
                          disabled={deleting === c.worker_contact_id}
                        >
                          {deleting === c.worker_contact_id ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            <i className="bi bi-trash"></i>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card-footer bg-white border-0 d-flex justify-content-between align-items-center py-3 px-4">
            <span className="text-muted small">
              Page {currentPage} of {totalPages}
            </span>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchContacts(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                  const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                  if (page > totalPages) return null;
                  return (
                    <li
                      key={page}
                      className={`page-item ${currentPage === page ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => fetchContacts(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                })}
                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchContacts(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 rounded-4 shadow-lg">
              <div className="modal-header border-0 px-4 pt-4 pb-0">
                <h5 className="modal-title fw-bold">
                  {editItem ? "Edit Contact" : "Add Contact"}
                </h5>
                <button
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSave}>
                <div
                  className="modal-body px-4 py-3"
                  style={{ maxHeight: "70vh", overflowY: "auto" }}
                >
                  {/* ── STEP 1: Village Autocomplete ── */}
                  <div
                    className="p-3 rounded-3 mb-3"
                    style={{ background: "#f0f4ff" }}
                  >
                    <p
                      className="small fw-semibold mb-2"
                      style={{ color: "#1a3c5e" }}
                    >
                      <i className="bi bi-geo-alt me-1"></i>
                      Step 1 — Select Village
                    </p>

                    <div className="position-relative" ref={villRef}>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i className="bi bi-search text-muted"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder="Type village name to search..."
                          value={villSearch}
                          onChange={(e) => handleVillSearch(e.target.value)}
                          autoComplete="off"
                        />
                        {selectedVill && (
                          <span className="input-group-text bg-white">
                            <i className="bi bi-check-circle-fill text-success"></i>
                          </span>
                        )}
                      </div>

                      {/* Village Dropdown */}
                      {showVillDrop && villDropdown.length > 0 && (
                        <div
                          className="border rounded-3 shadow bg-white position-absolute w-100 mt-1"
                          style={{
                            zIndex: 9999,
                            maxHeight: "160px",
                            overflowY: "auto",
                          }}
                        >
                          {villDropdown.map((v) => (
                            <div
                              key={v.villageid}
                              className="px-3 py-2 border-bottom d-flex align-items-center gap-2"
                              style={{ cursor: "pointer" }}
                              onMouseDown={() => selectVillage(v)}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "#f0f4ff")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "white")
                              }
                            >
                              <i className="bi bi-geo-alt text-muted small"></i>
                              <span className="small fw-medium">{v.name}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {villSearch &&
                        villDropdown.length === 0 &&
                        showVillDrop && (
                          <div
                            className="border rounded-3 bg-white position-absolute w-100 mt-1 px-3 py-2"
                            style={{ zIndex: 9999 }}
                          >
                            <span className="text-muted small">
                              No village found
                            </span>
                          </div>
                        )}
                    </div>

                    {selectedVill && (
                      <div className="mt-2 small text-success">
                        <i className="bi bi-check-circle me-1"></i>
                        Village selected: <strong>{selectedVill.name}</strong>
                      </div>
                    )}
                  </div>

                  {/* ── STEP 2: Voter Search ── */}
                  <div
                    className="p-3 rounded-3 mb-3"
                    style={{ background: selectedVill ? "#f0f4ff" : "#f8f9fa" }}
                  >
                    <p
                      className="small fw-semibold mb-2"
                      style={{ color: selectedVill ? "#1a3c5e" : "#aaa" }}
                    >
                      <i className="bi bi-person-search me-1"></i>
                      Step 2 — Search Voter from{" "}
                      {selectedVill ? (
                        <strong>{selectedVill.name}</strong>
                      ) : (
                        "selected village"
                      )}
                    </p>

                    <div className="position-relative" ref={voterRef}>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i className="bi bi-search text-muted"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder={
                            selectedVill
                              ? `Search voter in ${selectedVill.name}...`
                              : "Select village first..."
                          }
                          value={voterSearch}
                          onChange={(e) => handleVoterSearch(e.target.value)}
                          disabled={!selectedVill}
                          autoComplete="off"
                        />
                        {voterLoading && (
                          <span className="input-group-text bg-white">
                            <span className="spinner-border spinner-border-sm text-primary"></span>
                          </span>
                        )}
                      </div>
                      <div className="form-text">
                        {selectedVill
                          ? "Type 2+ characters — name (English/Kannada) or EPIC no"
                          : "Select a village above to enable voter search"}
                      </div>

                      {/* Voter Dropdown */}
                      {showVoterDrop && voterList.length > 0 && (
                        <div
                          className="border rounded-3 shadow bg-white position-absolute w-100 mt-1"
                          style={{
                            zIndex: 9999,
                            maxHeight: "240px",
                            overflowY: "auto",
                          }}
                        >
                          {voterList.map((v) => (
                            <div
                              key={v.voter_id}
                              className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center"
                              style={{ cursor: "pointer" }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "#f0f4ff")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "white")
                              }
                            >
                              <div>
                                <div className="fw-semibold small">
                                  {v.firstname_en} {v.lastname_en}
                                </div>
                                {(v.firstname_v1 || v.lastname_v1) && (
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: "12px" }}
                                  >
                                    {v.firstname_v1} {v.lastname_v1}
                                  </div>
                                )}
                                <div
                                  className="text-muted"
                                  style={{ fontSize: "11px" }}
                                >
                                  <span className="badge bg-primary-subtle text-primary me-1">
                                    {v.epic_no}
                                  </span>
                                  {v.mobile && (
                                    <span className="me-1">📱 {v.mobile}</span>
                                  )}
                                  <span>Booth {v.booth_no}</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm ms-2 text-white flex-shrink-0"
                                style={{
                                  background:
                                    "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                                }}
                                onMouseDown={() => selectVoter(v)}
                              >
                                <i className="bi bi-plus-lg me-1"></i>Select
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* No results */}
                      {showVoterDrop &&
                        voterSearch.length >= 2 &&
                        voterList.length === 0 &&
                        !voterLoading && (
                          <div
                            className="border rounded-3 bg-white position-absolute w-100 mt-1 px-3 py-2"
                            style={{ zIndex: 9999 }}
                          >
                            <span className="text-muted small">
                              <i className="bi bi-info-circle me-1"></i>
                              No voters found for "{voterSearch}"
                            </span>
                          </div>
                        )}
                    </div>
                  </div>

                  {/* ── STEP 3: Contact Details ── */}
                  <div
                    className="p-3 rounded-3"
                    style={{ background: "#f8f9fa" }}
                  >
                    <p
                      className="small fw-semibold mb-3"
                      style={{ color: "#1a3c5e" }}
                    >
                      <i className="bi bi-person-badge me-1"></i>
                      Step 3 — Contact Details
                    </p>
                    <div className="row g-3">
                      {/* Name */}
                      <div className="col-12 col-md-6">
                        <label className="form-label small fw-medium">
                          First Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="First name"
                          value={form.name}
                          onChange={(e) =>
                            setForm({ ...form, name: e.target.value })
                          }
                          required
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label small fw-medium">
                          Last Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Last name"
                          value={form.last_name}
                          onChange={(e) =>
                            setForm({ ...form, last_name: e.target.value })
                          }
                        />
                      </div>

                      {/* EPIC + Mobile */}
                      <div className="col-12 col-md-6">
                        <label className="form-label small fw-medium">
                          EPIC No
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Voter EPIC number"
                          value={form.epic_no}
                          onChange={(e) =>
                            setForm({ ...form, epic_no: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label small fw-medium">
                          Mobile No
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="10 digit mobile"
                          maxLength={10}
                          value={form.mobile_no}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, "");
                            setForm({ ...form, mobile_no: val });
                          }}
                        />
                      </div>

                      {/* Contact Type */}
                      <div className="col-12 col-md-6">
                        <label className="form-label small fw-medium">
                          Contact Type <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-select"
                          value={form.contact_type}
                          onChange={(e) =>
                            setForm({ ...form, contact_type: e.target.value })
                          }
                          required
                        >
                          {CONTACT_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Village (auto-filled from Step 1) */}
                      <div className="col-12 col-md-6">
                        <label className="form-label small fw-medium">
                          Village
                        </label>
                        <input
                          type="text"
                          className="form-control bg-white"
                          value={selectedVill ? selectedVill.name : ""}
                          placeholder="Auto-filled from Step 1"
                          readOnly
                        />
                      </div>

                      {/* Wing */}
                      {wingList.length > 0 && (
                        <div className="col-12 col-md-6">
                          <label className="form-label small fw-medium">
                            Wing
                          </label>
                          <select
                            className="form-select"
                            value={form.wingid}
                            onChange={(e) =>
                              setForm({ ...form, wingid: e.target.value })
                            }
                          >
                            <option value="">Select Wing</option>
                            {wingList.map((w) => (
                              <option key={w.wingid} value={w.wingid}>
                                {w.wingname}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Caste */}
                      {casteList.length > 0 && (
                        <div className="col-12 col-md-6">
                          <label className="form-label small fw-medium">
                            Caste
                          </label>
                          <select
                            className="form-select"
                            value={form.casteid}
                            onChange={(e) =>
                              setForm({ ...form, casteid: e.target.value })
                            }
                          >
                            <option value="">Select Caste</option>
                            {casteList.map((c) => (
                              <option key={c.casteid} value={c.casteid}>
                                {c.castename}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0 px-4 pb-4">
                  <button
                    type="button"
                    className="btn btn-light px-4"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn text-white px-4"
                    style={{
                      background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                    }}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg me-2"></i>
                        {editItem ? "Update" : "Save"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;
